import { cloneDeep } from 'lodash';
import { getFramework, trackConfig, type IFramework } from '@/framework';
import { type ContextMenuPlugin } from '@/plugins/context-menu';
import { LayerType, type ILayerProps } from '@/version-types';
import { type BackgroundLayer } from '../../../..';
import { CROP_BACKGROUND_EVENTS } from './event-name';

export class CropBackground {
    private framework: IFramework;
    /** 原始图层数据 */
    private originLayer: ILayerProps | null = null;
    /** 最新图层数据，可能会随时更新 */
    private latestLayer: ILayerProps | null = null;

    constructor(id: string) {
        this.framework = getFramework(id);
    }

    /** 设置最新图层数据 */
    setLatestLayer(layer: ILayerProps) {
        this.latestLayer = layer;
        this.framework.service.events.emit(CROP_BACKGROUND_EVENTS.SET_LATEST_LAYER, layer);
    }

    /** 获取最新图层数据 */
    getLatestLayer() {
        return this.latestLayer;
    }

    tryGetContextMenu() {
        const contextMenu = this.framework.service.registration.getInstance<ContextMenuPlugin>('ContextMenuPlugin');
        if (!contextMenu) {
            console.error('ContextMenuPlugin is not found');
            return null;
        }
        return contextMenu;
    }

    /** 打开裁剪 */
    open() {
        if (this.originLayer) return;

        const originIndex = this.framework.service.frame.getActiveFrameId();
        const backgroundLayer = this.framework.service.frame.getBackgroundLayer(originIndex);
        if (!backgroundLayer) return;
        this.framework.service.events.emit(CROP_BACKGROUND_EVENTS.CROP_OPEN);

        this.originLayer = cloneDeep(backgroundLayer);
        this.latestLayer = cloneDeep(backgroundLayer);

        this.tryGetContextMenu()?.disableMenu();
        this.framework.service.editor.setDisablePick(true);
        this.framework.service.edgelessCanvas.setHiddenAll(true);
    }

    cancel() {
        if (!this.originLayer) return;
        const layer = cloneDeep(this.originLayer);
        this.framework.service.frame.addOrUpdateLayer(layer);
        this.framework.service.events.emit(CROP_BACKGROUND_EVENTS.CROP_CANCEL);
        this.restoreAfterCrop(layer);
    }

    confirm() {
        if (!this.originLayer) return;
        let layer: ILayerProps | null = null;
        if (this.originLayer && this.latestLayer) {
            layer = this.latestLayer;
            this.framework.service.registration
                .getInstance<BackgroundLayer>('BackgroundLayer')
                .update(layer, true, trackConfig[LayerType.BACKGROUND_LAYER]?.crop, {
                    ...this.originLayer,
                });
        }
        this.framework.service.events.emit(CROP_BACKGROUND_EVENTS.CROP_CONFIRM);
        this.restoreAfterCrop(layer);
    }

    reset() {
        if (!this.originLayer) return;
        this.setLatestLayer(cloneDeep(this.originLayer));
        this.framework.service.frame.addOrUpdateLayer(cloneDeep(this.originLayer));
    }

    private restoreAfterCrop(layer: ILayerProps | null) {
        if (layer) {
            this.framework.service.history.realEdit.sendLayer(layer);
        }

        this.tryGetContextMenu()?.enableMenu();
        this.framework.service.editor.setDisablePick(false);
        this.framework.service.edgelessCanvas.setHiddenAll(false);

        this.originLayer = null;
        this.latestLayer = null;
        this.framework.service.events.emit(CROP_BACKGROUND_EVENTS.CROP_CLOSE);
    }

    /** 监听裁剪打开 */
    onOpen(fn: () => void) {
        return this.framework.service.events.on(CROP_BACKGROUND_EVENTS.CROP_OPEN, fn);
    }

    /** 监听裁剪关闭 */
    onClose(fn: () => void) {
        return this.framework.service.events.on(CROP_BACKGROUND_EVENTS.CROP_CLOSE, fn);
    }

    /** 监听裁剪取消 */
    onCancel(fn: () => void) {
        return this.framework.service.events.on(CROP_BACKGROUND_EVENTS.CROP_CANCEL, fn);
    }

    /** 监听裁剪确认 */
    onConfirm(fn: () => void) {
        return this.framework.service.events.on(CROP_BACKGROUND_EVENTS.CROP_CONFIRM, fn);
    }
}
